// Click-driven mega menu for a 3-level top-level item (e.g. SHOP).
// Column 1 = categories; clicking a category reveals its sub-column (column 2)
// with a left-to-right slide. Closes on outside click, Escape, or re-click.

class ShopMegaMenu extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-shop-mega-trigger]');
    this.panel = this.querySelector('[data-shop-mega-panel]');
    if (!this.trigger || !this.panel) return;

    this.cats = Array.from(this.panel.querySelectorAll('.shop-mega__cat'));
    this.subs = Array.from(this.panel.querySelectorAll('.shop-mega__sub'));
    this.subLinks = Array.from(this.panel.querySelectorAll('.shop-mega__sub-link'));
    this.subsubs = Array.from(this.panel.querySelectorAll('.shop-mega__subsub'));
    this.isOpen = false;

    // Move the panel to <body> so position:fixed is relative to the viewport.
    // (Header ancestors may use transforms, which trap a fixed-positioned child
    // and collapse its height.)
    if (this.panel.parentElement !== document.body) {
      document.body.appendChild(this.panel);
    }

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onReposition = this.positionPanel.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.cats.forEach((cat) => {
      // Column 2 follows the hovered first-column item, so it never keeps a stale
      // selection. Items without children clear it; clicking still navigates.
      cat.addEventListener('pointerenter', () => {
        if (cat.getAttribute('data-sub-target')) {
          this.selectCategory(cat);
        } else {
          this.clearSelection();
        }
      });
    });
    this.subLinks.forEach((link) => {
      // Column 3 follows the hovered second-column item, mirroring column 1 → 2.
      // Level-2 items with children reveal it; the others collapse it.
      link.addEventListener('pointerenter', () => {
        if (link.getAttribute('data-subsub-target')) {
          this.selectSub(link);
        } else {
          this.clearSubsub();
        }
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onReposition);
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  onDocClick(event) {
    // The panel lives in <body>, so check both the trigger element and the panel.
    if (!this.contains(event.target) && !this.panel.contains(event.target)) {
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.trigger.focus();
    }
  }

  // Dock the panel right below the header so the page stays visible beside it,
  // and align column 1 with the FIRST top-level nav item's text (e.g. SHOP) —
  // the same left edge for every mega menu, regardless of which item opens it.
  positionPanel() {
    const header =
      this.closest('#header-component') ||
      this.closest('.header') ||
      this.closest('header') ||
      document.getElementById('header-component');
    const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    this.panel.style.top = `${Math.max(bottom, 0)}px`;

    // Reference = the first nav item's title, not this mega's own trigger, so
    // column 1 always lines up under the first menu item.
    const list = this.closest('.menu-list__list') || this.closest('.menu-list');
    const firstTitle = list && list.querySelector('.menu-list__link-title');
    const titleEl = firstTitle || this.trigger.querySelector('.menu-list__link-title') || this.trigger;
    const rect = titleEl.getBoundingClientRect();
    const padLeft = parseFloat(getComputedStyle(titleEl).paddingLeft) || 0;
    this.panel.style.setProperty('--shop-mega-left', `${Math.max(rect.left + padLeft, 0)}px`);
  }

  open() {
    this.isOpen = true;
    this.positionPanel();
    this.panel.hidden = false;
    // Next frame so the slide transition runs from the hidden state.
    requestAnimationFrame(() => this.panel.classList.add('is-open'));
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.onReposition);
    window.addEventListener('scroll', this.onReposition, { passive: true });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.panel.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.clearSelection();
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onReposition);

    let done = false;
    const hide = () => {
      if (done) return;
      done = true;
      this.panel.removeEventListener('transitionend', hide);
      if (!this.isOpen) this.panel.hidden = true;
    };
    this.panel.addEventListener('transitionend', hide);
    setTimeout(hide, 450); // fallback if transitionend doesn't fire
  }

  selectCategory(cat) {
    // Switching first-column category changes column 2, so reset column 3.
    this.clearSubsub();
    const targetId = cat.getAttribute('data-sub-target');
    this.cats.forEach((c) => {
      const active = c === cat;
      c.classList.toggle('is-active', active);
      c.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
    this.subs.forEach((sub) => {
      sub.hidden = sub.id !== targetId;
    });
    this.panel.classList.add('has-sub');

    // Always replay the left-to-right slide on the revealed sub-column.
    const target = targetId ? this.panel.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (target) {
      target.classList.remove('shop-mega__sub--enter');
      void target.offsetWidth; // force reflow to restart the animation
      target.classList.add('shop-mega__sub--enter');
    }
  }

  clearSelection() {
    this.clearSubsub();
    this.cats.forEach((c) => {
      c.classList.remove('is-active');
      c.setAttribute('aria-expanded', 'false');
    });
    this.subs.forEach((sub) => {
      sub.hidden = true;
      sub.classList.remove('shop-mega__sub--enter');
    });
    this.panel.classList.remove('has-sub');
  }

  selectSub(link) {
    const targetId = link.getAttribute('data-subsub-target');
    this.subLinks.forEach((l) => {
      const active = l === link;
      l.classList.toggle('is-active', active);
      l.setAttribute('aria-expanded', active ? 'true' : 'false');
    });
    this.subsubs.forEach((ss) => {
      ss.hidden = ss.id !== targetId;
    });
    this.panel.classList.add('has-subsub');

    // Always replay the left-to-right slide on the revealed sub-sub-column.
    const target = targetId ? this.panel.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (target) {
      target.classList.remove('shop-mega__subsub--enter');
      void target.offsetWidth; // force reflow to restart the animation
      target.classList.add('shop-mega__subsub--enter');
    }
  }

  clearSubsub() {
    this.subLinks.forEach((l) => {
      l.classList.remove('is-active');
      l.setAttribute('aria-expanded', 'false');
    });
    this.subsubs.forEach((ss) => {
      ss.hidden = true;
      ss.classList.remove('shop-mega__subsub--enter');
    });
    this.panel.classList.remove('has-subsub');
  }
}

if (!customElements.get('shop-mega-menu')) {
  customElements.define('shop-mega-menu', ShopMegaMenu);
}
